/**
 * Pure helpers for the reward rule-builder.
 *
 * Kept free of React so the issue_reward config shape - the correctness-critical
 * part - can be unit-tested directly. The rule's `when` predicate is owned by the
 * shared <ConditionBuilder>; these helpers only compile/decompose the reward
 * config (amount, reward, account, template).
 */

/** Sentinel for "use the integration's connected default" in account/reward selects. */
export const USE_DEFAULT = '__default__'

export interface RuleFormValues {
  amount_mode?: string
  amount?: string
  amount_field?: string
  amount_percent?: string
  utid?: string
  account_identifier?: string
  etid?: string
  message?: string
}

export interface RuleFormState {
  amount_mode: string
  amount: string
  amount_field: string
  amount_percent: string
  utid: string
  account_identifier: string
  etid: string
  message: string
}

/** Decompose a stored rule's config back into dialog form values (edit). */
export function ruleToFormValues(
  config: Record<string, unknown> | undefined,
): RuleFormState {
  const c = config ?? {}
  const str = (v: unknown) => (v == null ? '' : String(v))
  return {
    amount_mode:
      c.amount_from == null
        ? 'fixed'
        : c.amount_percent != null
          ? 'percent'
          : 'claim_field',
    amount: str(c.amount),
    amount_field: str(c.amount_from),
    amount_percent: str(c.amount_percent),
    utid: c.utid ? String(c.utid) : USE_DEFAULT,
    account_identifier: c.account_identifier
      ? String(c.account_identifier)
      : USE_DEFAULT,
    etid: c.etid ? String(c.etid) : USE_DEFAULT,
    message: str(c.message),
  }
}

/** Build the issue_reward config from the dialog's form values. */
export function compileConfig(values: RuleFormValues): Record<string, unknown> {
  const config: Record<string, unknown> = {}
  if (values.amount_mode === 'percent') {
    if (values.amount_field) config.amount_from = values.amount_field
    if (values.amount_percent?.trim()) {
      config.amount_percent = Number(values.amount_percent)
    }
  } else if (values.amount_mode === 'claim_field') {
    if (values.amount_field) config.amount_from = values.amount_field
  } else if (values.amount?.trim()) {
    config.amount = Number(values.amount)
  }
  const utid = values.utid?.trim()
  if (utid && utid !== USE_DEFAULT) config.utid = utid
  const account = values.account_identifier?.trim()
  if (account && account !== USE_DEFAULT) config.account_identifier = account
  const etid = values.etid?.trim()
  if (etid && etid !== USE_DEFAULT) config.etid = etid
  if (values.message?.trim()) config.message = values.message.trim()
  return config
}
